import { Component, Vue, PropSync, Watch, Emit } from 'vue-property-decorator';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { ConfigurationField } from '@/models/configuration/Field';
import { NumberTypeEnum } from '@/enums/NumberTypeEnums';
import { FieldEventTypeEnums } from '@/enums/FieldEventTypeEnums';
import { ConfigurationValueSelect } from '@/models/configuration/ValuesSelect';
import { ApiIdentifers } from '@/config/index';
import { LoggerService, UnitsHelper } from '@/utils';
import { AxDataService, ConfigurationService, MyMillDataService } from '@/services/index';
import { MachineFieldTypeEnums } from '@/enums/MachineFieldTypeEnums';
import { Helpers } from '@/helpers/Helpers';
import { UserProfile } from '@/models/dexie/UserProfile';
import { UserProfileStore } from '@/store/store.userProfile';

export interface IFieldComponentMethods {
    validateField(): boolean;
    setFieldError(hasError: boolean): void;
}

export interface IFieldEvent {
    type: FieldEventTypeEnums;
    value: any;
    actionIndex?: number;
}

@Component
export default class FieldComponent extends Vue implements IFieldComponentMethods {
    @PropSync('field', { type: Object }) public syncedField!: ConfigurationField;
    @PropSync('fields', { type: Array }) public syncedFields!: ConfigurationField[];
    @PropSync('editValue', { type: Boolean }) public syncedEditValue!: boolean;
    @PropSync('previewReadonly', { type: Boolean, default: false }) public previewReadonlyValue!: boolean;

    public text: string = '';
    public searchText: string = '';
    public selectValue: number | string | ConfigurationValueSelect | null | undefined = null;
    public selectData: ConfigurationValueSelect[] = [];
    public maxTextLength: number | null = null;

    public axSelectData: ConfigurationValueSelect[] = [];
    public axSelectDisabled = true;
    public axDefaultText: string = '';
    public axIsLoading: boolean = false;

    public mymillSelectData: ConfigurationValueSelect[] = [];
    public mymillSelectDisabled = true;
    public mymillDefaultText: string = '';
    public mymillIsLoading: boolean = false;

    public numberValue: number | null = null;
    public numberValueString: string = '';
    public numberMin: number = 0;
    public numberMax: number = 100;
    public numberStep: number = 1;
    public numberType: NumberTypeEnum = NumberTypeEnum.INTEGER;

    public menu: boolean = false;
    public date: string = '';

    public menuTime: boolean = false;
    public datetime: string = '';

    public changeTimeout: any = null;
    public isValid: boolean = true;
    public unitInvalidOrMandatory: boolean = false;
    public unitFieldHint: string = '';
    public numberInvalidOrMandatory: boolean = false;

    public checkboxValue: boolean = false;

    public fte = FieldTypeEnum;

    private emitEvent: boolean = true;
    private lastIdentifier?: string;
    private skipNextCheck: boolean = false;
    private setMyMillDataOnLoaded: boolean = false;

    public async mounted() {
        await this.prepareField();
        this.setInitalValue();
    }

    public updated() {
        this.checkForClearButton();
    }

    public beforeDestroy() {
        AxDataService.OnSelectedCustomerChanged.off(this.getAxFieldData);
        AxDataService.OnAxFilteredCustomersChanged.off(this.setCustomerData);
        AxDataService.OnAxFilterCustomersStarted.off(this.setAxLoading);
        MyMillDataService.OnMyMillGetUnitsStarted.off(this.onMyMillLoading);
        MyMillDataService.OnMyMillSelectedUnitChanged.off(this.setMyMillUnits);
        MyMillDataService.OnMyMillUnitsChanged.off(this.setMyMillUnits);
    }

    public async prepareField() {
        if (this.syncedField.type === FieldTypeEnum.NUMBER) {
            this.numberMin = this.syncedField.min || 0;
            this.numberMax = this.syncedField.max || 100;
            this.numberStep = this.syncedField.rangeStep != null ? this.syncedField.rangeStep : this.getRangeStep();

            if (this.syncedField.identifier === 'd4aba6c6' && this.syncedField.valueNumber === 0) {
                this.numberValueString = '-';
                this.syncedField.valueNumber = null;
            }

            //if(this.syncedField.identifier)
            // if Blend% then set to 100% by default
            if (this.syncedField.identifier === ApiIdentifers.materialBlend && this.syncedField.valueNumber === undefined) {
                this.numberValue = 100;
                this.numberValueString = '100';
                this.syncedField.valueNumber = 100;
            }
        } else if (this.syncedField.type === FieldTypeEnum.DATE) {
            if (this.syncedField.valueString && this.syncedField.valueString!.length <= 0) {
                this.date = this.getDateString(new Date().toISOString());
            } else if (this.syncedField.valueString) {
                this.date = this.getDateString(this.syncedField.valueString);
            }
        } else if (this.syncedField.type === FieldTypeEnum.DATETIME) {
            if (this.syncedField.identifier === ApiIdentifers.generalTrialTime) {
                const datetimeField = this.syncedFields.find(field => field.identifier === ApiIdentifers.generalTrialDate);
                if (datetimeField != null) {
                    /**
                     * Since the datetime is ignored/not saved on server side
                     * Use the date field.
                     * Time is set also on date field if changed.
                     */
                    this.syncedField.valueString = `${datetimeField.valueString}`;
                }

                if (this.syncedField.valueString && this.syncedField.valueString!.length <= 0) {
                    this.datetime = this.getTimeString(new Date().toISOString());
                } else if (this.syncedField.valueString) {
                    this.datetime = this.getTimeString(this.syncedField.valueString);
                }
            }
        } else if (this.syncedField.type === FieldTypeEnum.TEXT || this.syncedField.type === FieldTypeEnum.TEXTAREA) {
            if (this.syncedField.max) {
                this.maxTextLength = this.syncedField.max;
            }
        } else if (this.syncedField.type === FieldTypeEnum.UNIT) {
            this.selectData = [];
            const unit = ConfigurationService.GetUnitsConfiguration.sections.find(sec => sec.identifier === this.syncedField.unitIdentifier);
            if (unit !== undefined && unit.values != null) {
                unit.values.forEach((element) => {
                    const item = new ConfigurationValueSelect(element);
                    item.value = Number(item.value);
                    this.selectData.push(item);
                });
            }
        } else if (this.syncedField.type === FieldTypeEnum.AXSELECT) {
            if (this.syncedField.identifier !== ApiIdentifers.axCustomerName) {
                this.axDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultAxField');
                AxDataService.OnSelectedCustomerChanged.on(this.getAxFieldData);
                /**
                 * In case we already have a selected customer,
                 * e.g. if we open a section that was not yet mounted
                 */
                if (AxDataService.selectedCustomer !== undefined) {
                    this.getAxFieldData();
                }
            } else {
                this.getAxFieldData();
                this.axDefaultText = '';
            }
        } else if (this.syncedField.type === FieldTypeEnum.MYMILL_SELECT) {
            if (this.syncedField.identifier !== ApiIdentifers.myMillUnit) {
                this.mymillDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultMyMillField');

                MyMillDataService.OnMyMillSelectedUnitChanged.off(this.getMyMillFieldData);
                MyMillDataService.OnMyMillSelectedUnitChanged.on(this.getMyMillFieldData);
                /**
                 * In case we already have a selected customer,
                 * e.g. if we open a section that was not yet mounted
                 */
                if (MyMillDataService.myMillSelectedUnit !== undefined) {
                    await this.getMyMillFieldData();
                }
            } else {
                await this.getMyMillFieldData();
                this.mymillDefaultText = '';
            }
        }
    }

    public onValueChanged() {
        if (ConfigurationField.isSpecialFunction(this.syncedField) === false) {
            this.checkIsValid();
            if (this.syncedField.type === FieldTypeEnum.CHECKBOX && this.emitEvent === true) {
                this.fieldEvent({ type: FieldEventTypeEnums.CHECKBOX, value: this.checkboxValue });
            } else if (this.syncedField.type === FieldTypeEnum.NUMBER && this.syncedField.unit !== undefined) {
                this.fieldEvent({ type: FieldEventTypeEnums.CHECK_UNIT_FIELD, value: this.syncedField.identifier });
            }
        } else {
            if (this.syncedField.type === FieldTypeEnum.CHECKBOX && this.syncedField.identifier === ApiIdentifers.machineOrderAll && this.emitEvent === true) {
                this.fieldEvent({ type: FieldEventTypeEnums.CHECKBOX_ALL, value: this.checkboxValue });
            }
        }

        if (this.emitEvent === false) {
            this.emitEvent = true;
        }
    }

    @Emit()
    public fieldEvent(event: IFieldEvent): IFieldEvent {
        return event;
    }

    @Emit()
    public unitChanged(field: ConfigurationField): ConfigurationField {
        return field;
    }

    @Watch('mymillSelectData', { deep: true })
    public onMymillSelectDataChanged(newValue: ConfigurationValueSelect) {
        LoggerService.debug(`mymillSelectData: `, newValue);
    }

    @Watch('mymillIsLoading', { deep: true })
    public onMymillIsLoadingChanged(newValue: boolean) {
        LoggerService.debug(`onMymillIsLoading: `, newValue);
    }

    @Watch('syncedField', { deep: true })
    public onSyncedFieldChanged(newValue: ConfigurationField) {
        this.emitEvent = false;
        if (this.lastIdentifier && this.lastIdentifier !== newValue.identifier) {
            this.prepareField();
        }
        this.lastIdentifier = newValue.identifier;
        this.setValue(newValue);
        this.checkIsValid();
    }

    @Watch('text')
    public onTextChanged(newVal?: string, oldVal?: string) {
        if (this.changeTimeout != null) {
            clearTimeout(this.changeTimeout);
            this.changeTimeout = null;
        }
        this.changeTimeout = setTimeout(() => {
            if (this.syncedField.identifier === ApiIdentifers.axSearchSerialnumber) {
                this.axIsLoading = true;
                if (newVal != null && newVal.length >= 5) {
                    AxDataService.FilterCustomersBySerialnumber(newVal).finally(() => {
                        this.axIsLoading = false;
                    });
                } else {
                    AxDataService.GetAllCustomerNames().finally(() => {
                        this.axIsLoading = false;
                    });
                }
            } else if (newVal != null && newVal.length === 0) {
                /**
                 * if text input is deleted manuel with del or backspace key
                 * we need to set the field undefined instead of empty string
                 */
                this.syncedField.valueString = undefined;
            } else {
                this.syncedField.valueString = newVal;
            }
            this.onValueChanged();
        }, 500);
    }

    @Watch('searchText')
    public onSearchTextChanged(newVal?: string, oldVal?: string) {
        if (this.changeTimeout != null) {
            clearTimeout(this.changeTimeout);
            this.changeTimeout = null;
        }
        this.changeTimeout = setTimeout(() => {
            if (this.syncedField.identifier === ApiIdentifers.myMillSerialNumber) {
                this.setMyMillLoading(true);
                if (newVal != null && newVal.length >= 5) {
                    MyMillDataService.SetFilteredMyMillUnits(newVal).finally(() => {
                        this.setMyMillLoading(false);
                    });
                } else {
                    MyMillDataService.GetAllUnitNames().finally(() => {
                        this.setMyMillLoading(false);
                    });
                }
            } else if (newVal != null && newVal.length === 0) {
                /**
                 * if text input is deleted manuel with del or backspace key
                 * we need to set the field undefined instead of empty string
                 */
                this.syncedField.valueString = undefined;
            } else {
                this.syncedField.valueString = newVal;
            }
            this.onValueChanged();
        }, 500);
    }

    // @Watch('numberValueString')
    // public async onNumberValueStringChanged(newVal: string, oldVal: string) {
    //     if (newVal !== oldVal) {
    //         if (this.changeTimeout != null) {
    //             clearTimeout(this.changeTimeout);
    //             this.changeTimeout = null;
    //         }
    //         this.changeTimeout = setTimeout(() => {
    //             this.numberValue = parseFloat(newVal);
    //         }, 500);
    //     } else {
    //         LoggerService.LogDebug(`onNumberValueStringChanged value did not change: ${oldVal} --> ${newVal}`);
    //     }
    // }
    public async onNumberValueStringChanged(newVal: string) {
        if (this.changeTimeout != null) {
            clearTimeout(this.changeTimeout);
            this.changeTimeout = null;
        }
        this.changeTimeout = setTimeout(() => {
            this.validateField();
            this.numberValue = parseFloat(newVal);
        }, 500);
    }

    @Watch('numberValue')
    public async onNumberValueChanged(newVal?: number, oldVal?: number) {
        if (newVal == null || isNaN(newVal)) {
            this.syncedField.valueNumber = undefined;
        } else {
            this.syncedField.valueNumber = this.numberValue;
        }
        await this.testNumber();
        this.onValueChanged();
    }

    @Watch('selectValue')
    public onSelectValueChanged(newVal?: number | string | ConfigurationValueSelect) {
        if (newVal == null) {
            if (this.setMyMillDataOnLoaded === false) {
                this.syncedField.valueString = undefined;
                this.syncedField.valueNumber = undefined;
                this.syncedField.valueBool = undefined;
            }
            // if customer name is cleared, clear the other customer fields too
            if (this.syncedField.identifier === ApiIdentifers.axCustomerName) {
                AxDataService.SetSelectedCustomer();
            } else if (this.syncedField.identifier === ApiIdentifers.myMillUnit) {
                MyMillDataService.SetSelectedUnit();
            }
        } else if (this.syncedField.type === FieldTypeEnum.AXSELECT || this.syncedField.type === FieldTypeEnum.MYMILL_SELECT) {
            if (typeof newVal === 'object') {
                this.syncedField.valueString = newVal.value.toString();
            } else {
                this.syncedField.valueString = newVal.toString();
            }
            if (this.syncedField.identifier === ApiIdentifers.axCustomerName) {
                AxDataService.SetSelectedCustomer(this.syncedField.valueString);
            } else if (this.syncedField.identifier === ApiIdentifers.myMillUnit) {
                MyMillDataService.SetSelectedUnit(this.syncedField.valueString);
            }
        } else if (this.syncedField.identifier === ApiIdentifers.generalTemperatureFormat) {
            const numericValue = this.getSelectNumericValue(newVal);
            if (numericValue != null) {
                ConfigurationService.SetUsedTemperatureFormat(numericValue);
                this.syncedField.valueNumber = numericValue;
                this.selectValue = numericValue;
            }
        } else {
            const numericValue = this.getSelectNumericValue(newVal);
            if (numericValue != null) {
                this.syncedField.valueNumber = numericValue;
                if (this.syncedField.type === FieldTypeEnum.UNIT || this.syncedField.type === FieldTypeEnum.SELECT) {
                    this.selectValue = numericValue;
                }
            }
        }
        this.onValueChanged();
    }

    @Watch('date')
    public onDateChanged(newVal: string, oldVal: string) {
        const tempDate = new Date(newVal);
        this.syncedField.valueString = tempDate.toISOString();

        const timeField = this.syncedFields.find(f => f.identifier === ApiIdentifers.generalTrialTime);
        if (timeField) {
            let datetimeTime = new Date(timeField.valueString || '');
            if (!isNaN(datetimeTime.getTime())) {
                datetimeTime.setFullYear(tempDate.getFullYear());
                datetimeTime.setMonth(tempDate.getMonth());
                datetimeTime.setDate(tempDate.getDate());
            } else {
                datetimeTime = new Date(tempDate);
            }
            timeField.valueString = datetimeTime.toISOString();
        }

        this.onValueChanged();
    }

    @Watch('datetime')
    public onDateTimeChanged(newVal: string, oldVal: string) {
        const timeparts = newVal.split(':');
        let tempDatetime = new Date();
        const dateField = this.syncedFields.find(f => f.identifier === ApiIdentifers.generalTrialDate);
        if (dateField && dateField.valueString && dateField.isValid) {
            tempDatetime = new Date(dateField.valueString);
        }

        tempDatetime.setHours(parseInt(timeparts[0], 10));
        tempDatetime.setMinutes(parseInt(timeparts[1], 10));
        tempDatetime.setSeconds(0);
        tempDatetime.setMilliseconds(0);

        this.syncedField.valueString = tempDatetime.toISOString();
        if (dateField) {
            /**
             * Also update the date field when time is changed
             * to set time there also so we only need to check one
             * field in BE
             */
            dateField.valueString = tempDatetime.toISOString();
        }
        this.onValueChanged();
    }

    get getUserProfile(): UserProfile {
        return UserProfileStore.userProfile;
    }

    // @Watch('checkboxValue')
    // public onCheckboxValueChanged(newVal: boolean, oldVal: boolean) {
    //     /* Somehow there is a case where newValue is undefined, not sure how this happens
    //      * ignore this case for now, investigate later?
    //      */
    //     if (newVal !== undefined) {
    //         this.syncedField.valueBool = newVal;
    //         this.onValueChanged();
    //     } else {
    //         LoggerService.LogDebug(`skipping onCheckboxValueChanged ${oldVal} --> ${newVal}`);
    //     }
    // }

    public onCheckboxValueChanged(inEvent: boolean) {
        if (this.getCheckBoxDisabled() === false) {
            this.emitEvent = true;
            this.syncedField.valueBool = inEvent;
            this.onValueChanged();
        } else {
            /**
             * Workarround to disable button in create/edit form
             * when machine section was added via the add button
             */
            this.$nextTick(() => {
                this.checkboxValue = this.syncedField.valueBool || false;
            });
        }
    }

    public onFocus() {
        if (this.menu === false) {
            this.menu = true;
        }
    }

    public onFocusTime() {
        this.menuTime = true;
    }

    @Watch('isValid')
    public onIsValidChanged(newVal: boolean, oldVal: boolean) {
        LoggerService.LogDebug(`isValid ${oldVal} --> ${newVal}`);
    }

    public getTypeAsString(type: FieldTypeEnum): string {
        let typeString = '';
        typeString = FieldTypeEnum[type];

        return typeString;
    }

    public getSelectData() {
        this.selectData.splice(0, this.selectData.length);
        if (this.syncedField.values && this.syncedField.values.length > 0) {
            this.syncedField.values.forEach((element: any) => {
                this.selectData.push(new ConfigurationValueSelect(element));
            });
        }
        return this.selectData;
    }

    public async testNumber() {
        LoggerService.LogDebug(`testNumber: ${this.numberValue}`);
        const self = this;

        if (this.numberValue != null && this.syncedField.numericType === NumberTypeEnum.INTEGER) {
            await this.$nextTick(() => {
                if (self.numberValue) {
                    self.numberValue = parseInt(self.numberValue.toFixed(0), 10);
                    self.numberValueString = self.numberValue.toString();
                }
            });
        } else if (this.numberValue != null && this.syncedField.numericType === NumberTypeEnum.DOUBLE) {
            await this.$nextTick(() => {
                // 1. Break down the path to UnitsHelper safely
                const profile = UnitsHelper.getUserProfile;
                const units = profile && profile.units ? profile.units : [];

                if (self.numberValue != null && this.syncedField.rangeStep) {
                    // 2. Default to 0 or a safe fallback if the array is empty
                    let selectedValue = 0;
                    if (units.length > 0 && units[0]) {
                        selectedValue = units[0].selectedValue;
                    }

                    // 3. Logic with safe selectedValue
                    const decimal =
                        selectedValue === 4
                            ? 1
                            : selectedValue === 1
                            ? 2
                            : selectedValue === 3 || selectedValue === 2
                            ? self.getPrecision(this.syncedField.rangeStep)
                            : self.getPrecision(this.syncedField.rangeStep);

                    // 4. Force the UI to update
                    const fixedVal = parseFloat(self.numberValue.toFixed(decimal));
                    self.numberValue = fixedVal;
                    self.numberValueString = fixedVal.toString();
                }
            });
        } else if (this.numberValue != null) {
            await this.$nextTick(() => {
                if (self.numberValue) {
                    const decimal =
                        UnitsHelper.getUserProfile.units[0].selectedValue === 4
                            ? 1
                            : UnitsHelper.getUserProfile.units[0].selectedValue === 1
                            ? 2
                            : UnitsHelper.getUserProfile.units[0].selectedValue === 3 || UnitsHelper.getUserProfile.units[0].selectedValue === 2
                            ? 3
                            : self.getPrecision(self.numberStep);

                    self.numberValue = parseFloat(self.numberValue.toFixed(decimal));
                    self.numberValueString = self.numberValue.toString();
                }
            });
        }

        if (this.numberValue != null && this.numberValue > this.numberMax) {
            await this.$nextTick(() => {
                self.numberValue = self.numberMax;
                self.numberValueString = self.numberMax.toString();
            });
        } else if (this.numberValue != null && this.numberValue < this.numberMin) {
            await this.$nextTick(() => {
                self.numberValue = self.numberMin;
                self.numberValueString = self.numberMin.toString();
            });
        }
    }

    public allowedDates(inValue: any) {
        // LoggerService.LogDebug(`allowedDates: `, inValue);
        const now = new Date();
        let isValid = true;
        if (new Date(inValue).getTime() > now.getTime()) {
            isValid = false;
        }
        return isValid;
    }

    public validateField(forceCheck: boolean = false): boolean {
        return this.checkIsValid(forceCheck);
    }

    public up() {
        // previous week
        const pickedDate = new Date(this.date);
        pickedDate.setDate(pickedDate.getDate() - 7);
        this.date = pickedDate.toISOString().substring(0, 10);
    }

    public down() {
        // next week
        const pickedDate = new Date(this.date);
        pickedDate.setDate(pickedDate.getDate() + 7);
        this.date = pickedDate.toISOString().substring(0, 10);
    }

    public left(e: KeyboardEvent) {
        // previous month with shift key otherwise previous day
        const pickedDate = new Date(this.date);
        if (e.shiftKey) {
            pickedDate.setMonth(pickedDate.getMonth() - 1);
        } else {
            pickedDate.setDate(pickedDate.getDate() - 1);
        }
        this.date = pickedDate.toISOString().substring(0, 10);
    }

    public right(e: KeyboardEvent) {
        // next month with shift key otherwise next day
        const pickedDate = new Date(this.date);
        if (e.shiftKey) {
            pickedDate.setMonth(pickedDate.getMonth() + 1);
        } else {
            pickedDate.setDate(pickedDate.getDate() + 1);
        }
        this.date = pickedDate.toISOString().substring(0, 10);
    }

    /**
     * Method to fetch translation with fallback
     * @param field ConfigurationSection
     */
    public getIdentifierText(): string {
        let ret: string = '';
        if (this.syncedField && this.syncedField.identifier) {
            ret = this.$vuetify.lang.t(`$vuetify.identifiers.${this.syncedField.identifier}`);

            if (ret.length === 0 || ret.includes('$vuetify.identifiers.')) {
                ret = this.syncedField.name;
            }
        }
        if (this.syncedField.identifier === 'd4aba6c6' && this.previewReadonlyValue) {
            ret = '-';
        }

        return ret;
    }

    public async getAxFieldData(inSelectedCustomer?: string) {
        if (this.syncedField.identifier === ApiIdentifers.axCustomerName) {
            this.axSelectDisabled = false;
            this.axDefaultText = '';
            AxDataService.OnAxFilterCustomersStarted.off(this.setAxLoading);
            AxDataService.OnAxFilteredCustomersChanged.off(this.setCustomerData);
            AxDataService.OnAxFilterCustomersStarted.on(this.setAxLoading);
            AxDataService.OnAxFilteredCustomersChanged.on(this.setCustomerData);
            await AxDataService.GetAllCustomerNames()
                .then(data => {
                    this.axSelectData = AxDataService.axFilteredCustomers;
                })
                .finally(() => {
                    this.axIsLoading = false;
                });
        } else {
            if (inSelectedCustomer != null || AxDataService.selectedCustomer !== undefined) {
                let preselect = true;
                this.axDefaultText = '';
                switch (this.syncedField.identifier) {
                    case ApiIdentifers.axCustomerNumber:
                        this.axSelectData = AxDataService.selectedCustomerNumbers;
                        break;
                    case ApiIdentifers.axCountry:
                        this.axSelectDisabled = false;
                        this.axSelectData = AxDataService.axAllCountries;
                        break;
                    case ApiIdentifers.generalAXProject:
                        this.axSelectDisabled = false;
                        this.axSelectData = AxDataService.selectedCustomerProjectNumbers;
                        break;
                    case ApiIdentifers.axAddress:
                        this.axSelectDisabled = false;
                        this.axSelectData = AxDataService.selectedCustomerAddresses;
                        break;
                    default:
                        this.axSelectDisabled = false;
                        await this.checkAxMachineField(this.syncedField.identifier);
                        preselect = false;
                        break;
                }

                if (this.axSelectData.length > 0) {
                    if (this.syncedField.identifier === ApiIdentifers.axCountry && AxDataService.selectedCustomerCountries.length > 0) {
                        this.selectValue = AxDataService.selectedCustomerCountries[0].value;
                    } else if (preselect === true) {
                        this.selectValue = this.axSelectData[0].value;
                    }
                }
            } else {
                this.selectValue = undefined;
                this.axSelectData = [];
                this.axSelectDisabled = true;
                this.axDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultAxField');
            }
        }
    }

    public async getMyMillFieldData() {
        if (this.syncedField.identifier === ApiIdentifers.myMillUnit) {
            this.mymillSelectDisabled = false;
            this.mymillDefaultText = '';

            MyMillDataService.OnMyMillGetUnitsStarted.off(this.onMyMillLoading);
            MyMillDataService.OnMyMillUnitsChanged.off(this.setMyMillUnits);
            MyMillDataService.OnMyMillGetUnitsStarted.on(this.onMyMillLoading);
            MyMillDataService.OnMyMillUnitsChanged.on(this.setMyMillUnits);

            if (this.syncedField.valueString != null) {
                this.setMyMillDataOnLoaded = true;
            }

            await MyMillDataService.GetAllUnitNames()
                .then(units => {
                    this.mymillSelectData = units;
                    if (this.setMyMillDataOnLoaded === true) {
                        this.setMyMillDataOnLoaded = false;
                        this.selectValue = this.syncedField.valueString != null ? this.syncedField.valueString : null;
                    }
                })
                .finally(() => {
                    this.setMyMillLoading(false);
                });
        } else {
            if (MyMillDataService.myMillSelectedUnit !== undefined) {
                let preselect = true;
                this.mymillDefaultText = '';
                switch (this.syncedField.identifier) {
                    case ApiIdentifers.myMillMachine:
                        this.mymillSelectDisabled = false;
                        this.mymillSelectData = MyMillDataService.myMillSelectedUnitMachines;
                        break;
                    default:
                        this.mymillSelectDisabled = false;
                        preselect = false;
                        break;
                }

                if (this.mymillSelectData.length > 0) {
                    if (this.setMyMillDataOnLoaded === true) {
                        this.setMyMillDataOnLoaded = false;
                        this.selectValue = this.syncedField.valueString != null ? this.syncedField.valueString : null;
                    } else if (preselect === true) {
                        this.selectValue = this.mymillSelectData[0].value;
                    }
                }
            } else {
                this.selectValue = undefined;
                this.mymillSelectData = [];
                this.mymillSelectDisabled = true;
                this.mymillDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultMyMillField');
            }
        }
    }

    public axDataFilter(item: ConfigurationValueSelect, queryText: string, itemText: string): boolean {
        const textOne = item.text.toLowerCase();
        const textTwo = (item.value as string).toLowerCase();
        const searchText = queryText.toLowerCase();

        return textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1;
    }

    public mymillDataFilter(item: ConfigurationValueSelect, queryText: string, itemText: string): boolean {
        const textOne = item.text.toLowerCase();
        const textTwo = (item.value as string).toLowerCase();
        const searchText = queryText.toLowerCase();

        return textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1;
    }

    public getMyMillSelectedUnitNameString(inData: ConfigurationValueSelect): string {
        let name = '';
        if (inData && inData.text && inData.subText) {
            name = `${inData.text} (${inData.subText})`;
        } else if (inData && inData.text) {
            name = `${inData.text}`;
        }

        return name;
    }

    public setFieldError(hasError: boolean) {
        if (hasError === true) {
            const self = this;
            this.$nextTick(() => {
                self.syncedField.isValid = false;
                self.numberInvalidOrMandatory = true;
                self.skipNextCheck = true;
            });
        }
    }

    public getCheckBoxDisabled(): boolean {
        /**
         * previewReadonlyValue is by default false so use it as initial value.
         * If syncedField.disabled is set and previewReadonlyValue is false, then
         * override it.
         */
        let disabled = this.previewReadonlyValue;
        if (this.syncedField.disabled != null && !disabled) {
            disabled = this.syncedField.disabled;
        }
        return disabled;
    }

    public onDateChangedMenu(inDate: string): void {
        if (this.$refs.menu && (this.$refs.menu as any).save) {
            (this.$refs.menu as any).save(inDate);
            if (this.menu !== false) {
                this.menu = false;
            }
            LoggerService.debug(`this.menu: ${this.menu}`);
        }
    }

    public onDateTimeChangedMenu(inDateTime: string): void {
        if (this.$refs.menuTime && (this.$refs.menuTime as any).save) {
            (this.$refs.menuTime as any).save(inDateTime);
            this.menuTime = false;
        }
    }

    private async checkAxMachineField(inIdentifier: string) {
        const parent = ApiIdentifers.GetSectionIdentifier(inIdentifier);
        if (parent && parent !== '') {
            const fieldType = ApiIdentifers.GetMachineFieldType(inIdentifier);
            const data = await AxDataService.getMachineModelsBySection(parent);
            let values: string[] = [];
            data.forEach(mm => {
                switch (fieldType) {
                    case MachineFieldTypeEnums.MODEL:
                        values.push(mm.model);
                        break;
                    case MachineFieldTypeEnums.COMMISSON_NUMBER:
                        values = values.concat(mm.commissonNumbers || []);
                        break;
                    case MachineFieldTypeEnums.SERIAL_NUMBER:
                        values = values.concat(mm.serialNumbers || []);
                        break;
                    case MachineFieldTypeEnums.SOFTWARE_VERSION:
                        values.push(mm.model);
                        break;
                    default:
                        break;
                }
            });
            values.forEach(v => {
                this.axSelectData.push(
                    new ConfigurationValueSelect({
                        text: v,
                        value: v
                    })
                );
            });
        }
    }

    private checkIsValid(forceCheck: boolean = false): boolean {
        if (this.syncedField === undefined) {
            LoggerService.LogWarn(`checkIsValid has no data`);
            return false;
        }

        /**
         * This is for the unit field check.
         * If a unit is selected but not value
         * entered we need to skip one check to
         * mark the field correct
         */
        if (this.skipNextCheck === true) {
            this.skipNextCheck = false;
            return false;
        }

        if (this.syncedField.required === true || forceCheck === true) {
            this.syncedField.isValid = this.checkValue();
            this.isValid = this.syncedField.isValid;
        } else if ((this.syncedField.type === FieldTypeEnum.TEXTAREA || this.syncedField.type === FieldTypeEnum.TEXT) && this.maxTextLength != null && this.maxTextLength > 0) {
            this.syncedField.isValid = this.checkValue();
            this.isValid = this.syncedField.isValid;
        }

        if (this.syncedField.type === FieldTypeEnum.UNIT) {
            this.unitFieldHint = this.isMyMillTemperatureFormatFahrenheitInvalid()
                ? this.$vuetify.lang.t('$vuetify.edit.mymillTemperatureFormatHint')
                : '';

            if (this.syncedField.required === false && forceCheck === false) {
                this.unitInvalidOrMandatory = false;
            } else {
                if (this.isValid === true) {
                    this.unitInvalidOrMandatory = false;
                } else {
                    this.unitInvalidOrMandatory = true;
                }
            }

            if (this.isMyMillTemperatureFormatFahrenheitInvalid()) {
                this.unitInvalidOrMandatory = true;
                this.isValid = false;
                this.syncedField.isValid = false;
            }
        } else if (this.syncedField.type === FieldTypeEnum.NUMBER) {
            if (this.isValid === true) {
                this.numberInvalidOrMandatory = false;
            } else {
                this.numberInvalidOrMandatory = true;
            }
        }

        return this.isValid || false;
    }

    private getRangeStep(): number {
        let numberStringArray = this.numberMax.toString().split('.');
        if (numberStringArray.length === 1) {
            numberStringArray = this.numberMin.toString().split('.');
        }

        let result: string = '1';
        if (numberStringArray.length > 1) {
            for (let i = 1; i < numberStringArray[1].length; i++) {
                result = '0' + result;
            }
            result = '0.' + result;
        }

        return parseFloat(result);
    }

    private getPrecision(inNumber: number): number {
        const parts = inNumber.toString().split('.');
        let precision = 0;
        if (parts.length > 1) {
            precision = parts[1].length;
        }
        return precision;
    }

    private setValue(newValue: ConfigurationField) {
        if (this.syncedEditValue && this.syncedEditValue === true) {
            switch (this.syncedField.type) {
                case FieldTypeEnum.CHECKBOX:
                    this.checkboxValue = this.syncedField.valueBool || false;
                    break;
                case FieldTypeEnum.DATE:
                    this.date = this.getDateString(this.syncedField.valueString || '');
                    break;
                case FieldTypeEnum.DATETIME:
                    this.datetime = this.getTimeString(this.syncedField.valueString);
                    break;
                case FieldTypeEnum.NUMBER:
                    this.numberValue = this.syncedField.valueNumber != null ? this.syncedField.valueNumber : null;
                    this.numberValueString = this.syncedField.valueNumber != null ? this.syncedField.valueNumber.toString() : '';
                    break;
                case FieldTypeEnum.TEXT:
                case FieldTypeEnum.TEXTAREA:
                case FieldTypeEnum.SEARCH:
                    this.text = this.syncedField.valueString || '';
                    break;
                case FieldTypeEnum.SELECT:
                case FieldTypeEnum.UNIT:
                    this.selectValue = this.syncedField.valueNumber != null ? this.syncedField.valueNumber : null;
                    break;
                case FieldTypeEnum.AXSELECT:
                case FieldTypeEnum.MYMILL_SELECT:
                    this.selectValue = this.syncedField.valueString != null ? this.syncedField.valueString : null;
                    break;
                default:
                    break;
            }
        } else {
            switch (this.syncedField.type) {
                case FieldTypeEnum.CHECKBOX:
                    this.checkboxValue = newValue.valueBool || false;
                    break;
                case FieldTypeEnum.UNIT:
                    this.selectValue = newValue.valueNumber;
                    break;
                default:
                    break;
            }
        }
    }

    private checkValue(): boolean {
        let isValid = false;
        switch (this.syncedField.type) {
            case FieldTypeEnum.CHECKBOX:
                isValid = this.checkboxValue !== undefined ? this.checkboxValue : false;
                break;
            case FieldTypeEnum.DATE:
                isValid = this.date !== undefined && this.date.length > 0 ? true : false;
                break;
            case FieldTypeEnum.DATETIME:
                isValid = this.datetime !== undefined && this.datetime.length > 0 ? true : false;
                break;
            case FieldTypeEnum.NUMBER:
                isValid = this.numberValue !== undefined && this.numberValue !== null ? this.checkIsNumber(this.numberValue) : false;
                break;
            case FieldTypeEnum.SELECT:
            case FieldTypeEnum.UNIT: {
                const selectNumericValue = this.getSelectNumericValue(this.selectValue as number | string | ConfigurationValueSelect | null);
                isValid = selectNumericValue !== undefined && selectNumericValue !== null ? this.checkIsNumber(selectNumericValue) : false;
                if (isValid && this.isMyMillTemperatureFormatFahrenheitInvalid()) {
                    isValid = false;
                }
                break;
            }
            case FieldTypeEnum.AXSELECT:
            case FieldTypeEnum.MYMILL_SELECT:
                isValid = this.selectValue !== undefined && this.selectValue !== null ? this.selectValue.toString().length > 0 : false;
                break;
            case FieldTypeEnum.TEXT:
            case FieldTypeEnum.TEXTAREA:
            case FieldTypeEnum.SEARCH:
                if (this.text != null && (this.syncedField.required ? this.text.length > 0 : this.text.length >= 0)) {
                    if (this.maxTextLength != null) {
                        isValid = this.text.length <= this.maxTextLength;
                    } else {
                        isValid = true;
                    }
                } else if (this.text == null && this.syncedField.required === false) {
                    isValid = true;
                } else {
                    isValid = false;
                }
                break;
            default:
                break;
        }

        return isValid;
    }

    private setInitalValue() {
        switch (this.syncedField.type) {
            case FieldTypeEnum.CHECKBOX:
                this.checkboxValue = this.syncedField.valueBool || false;
                break;
            case FieldTypeEnum.DATE:
                if (this.syncedField.identifier === ApiIdentifers.generalTrialDate) {
                    this.date = this.getDateString(this.syncedField.valueString || new Date().toISOString().substring(0, 10));
                } else {
                    this.date = this.getDateString((this.syncedField.valueString && this.syncedField.valueString.substring(0, 10)) || '');
                }
                break;
            case FieldTypeEnum.DATETIME:
                if (this.syncedField.identifier === ApiIdentifers.generalTrialTime) {
                    this.datetime = this.getTimeString(this.syncedField.valueString);
                } else {
                    this.datetime = this.getTimeString(this.syncedField.valueString);
                }
                break;
            case FieldTypeEnum.NUMBER:
                this.numberValue = this.syncedField.valueNumber != null ? this.syncedField.valueNumber : null;
                this.numberValueString = this.syncedField.valueNumber != null ? this.syncedField.valueNumber.toString() : '';

                if (this.numberValue != null && this.syncedField.unit != null) {
                    let value = '';
                    if (this.syncedField.unit === ApiIdentifers.unitTemperature) {
                        // handle temperature extra, format is selected by user profile
                        value = UnitsHelper.calcByUnit(this.syncedField.unit, this.syncedField.rangeStep, this.syncedField.valueNumber != null ? this.syncedField.valueNumber : 0, 0);
                    } else {
                        const unitField = this.syncedFields.find(x => x.identifier === this.syncedField.unit);
                        if (unitField !== undefined && unitField.unitIdentifier !== undefined) {
                            value = UnitsHelper.calcByUnit(
                                unitField.unitIdentifier,
                                this.syncedField.rangeStep,
                                this.syncedField.valueNumber != null ? this.syncedField.valueNumber : 0,
                                unitField.valueNumber || undefined
                            );
                        }
                    }
                    this.numberValue = parseFloat(value);
                    this.numberValueString = value;
                }
                break;
            case FieldTypeEnum.TEXT:
            case FieldTypeEnum.TEXTAREA:
            case FieldTypeEnum.SEARCH:
                this.text = this.syncedField.valueString || '';
                break;
            case FieldTypeEnum.SELECT:
            case FieldTypeEnum.UNIT:
                // TODO: check how to set this value correct
                // const selectValue = this.selectValue.find
                LoggerService.LogWarn(`Set selectValue may not work corect, please check!`);
                if (this.syncedField.identifier === ApiIdentifers.generalTemperatureFormat) {
                    /**
                     * Check if we have a selected temperature unit format in user settings
                     * TODO: add constant to Identifiers class
                     */
                    const userProfileFormat = this.getUserProfile.units.find(upf => upf.unitIdentifier === this.syncedField.unitIdentifier);
                    if (userProfileFormat != null) {
                        this.selectValue = userProfileFormat.selectedValue;
                    } else {
                        this.selectValue = this.syncedField.valueNumber ? this.syncedField.valueNumber : 0;
                    }
                } else if (this.syncedField.identifier === ApiIdentifers.generalUnitFormat) {
                    this.selectValue = this.syncedField.valueNumber ? this.syncedField.valueNumber : 0;
                } else {
                    const userProfileFormat = this.getUserProfile.units.find(upf => upf.unitIdentifier === this.syncedField.unitIdentifier || '');
                    if (userProfileFormat && userProfileFormat != null) {
                        this.selectValue = userProfileFormat.selectedValue;
                    } else {
                        this.selectValue =
                            this.syncedField.valueNumber != null ? this.syncedField.valueNumber : this.syncedField.valueBool != null ? (this.syncedField.valueBool === true ? 1 : 0) : null;
                    }
                }
                break;
            case FieldTypeEnum.AXSELECT:
                this.selectValue = this.syncedField.valueString != null ? this.syncedField.valueString : null;
                if (this.selectValue && this.syncedField.identifier === ApiIdentifers.axCustomerName) {
                    this.axSelectDisabled = true;
                } else if (this.selectValue && this.syncedField.identifier === ApiIdentifers.axCustomerNumber) {
                    this.axSelectDisabled = true;
                    this.axDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultAxField');
                } else if (this.syncedField.identifier === ApiIdentifers.axCustomerNumber) {
                    this.axSelectDisabled = false;
                }
                break;
            case FieldTypeEnum.MYMILL_SELECT:
                if (this.syncedField.identifier === ApiIdentifers.myMillMachine) {
                    this.setMyMillDataOnLoaded = true;
                }
                // this.selectValue = this.syncedField.valueString != null ? this.syncedField.valueString : null;
                // if (this.selectValue && this.syncedField.identifier === ApiIdentifers.myMillUnit) {
                //     this.mymillSelectDisabled = false;
                // } else if (this.selectValue && this.syncedField.identifier === ApiIdentifers.myMillMachine) {
                //     this.mymillSelectDisabled = false;
                //     this.mymillDefaultText = this.$vuetify.lang.t('$vuetify.edit.defaultAxField');
                // }
                break;
            default:
                break;
        }
    }

    private checkIsNumber(inNumber: number): boolean {
        return !isNaN(inNumber);
    }

    private isMyMillTemperatureFormatFahrenheitInvalid(): boolean {
        const routeName = this.$route && this.$route.name;
        if (routeName !== 'myMillWizard' && routeName !== 'myMillWizardUnsynced') {
            return false;
        }
        if (this.syncedField.identifier !== ApiIdentifers.generalTemperatureFormat) {
            return false;
        }
        const selectNumericValue = this.getSelectNumericValue(this.selectValue as number | string | ConfigurationValueSelect | null);
        return selectNumericValue === 1;
    }

    private getSelectNumericValue(inValue?: number | string | ConfigurationValueSelect | null): number | undefined {
        if (inValue == null) {
            return undefined;
        }
        if (typeof inValue === 'object') {
            const numericValue = inValue.value;
            return typeof numericValue === 'number' ? numericValue : parseInt(numericValue as string, 10);
        }
        return typeof inValue === 'number' ? inValue : parseInt(inValue, 10);
    }

    private checkForClearButton() {
        /**
         * this check removes the clear button from the tab index
         * so that you can't reach it via tabulator key.
         * See Ticket: https://appsfactory.atlassian.net/browse/TTDB-678
         */
        const fieldRef = Helpers.getReference<Vue>(this.$refs, 'field');
        if (fieldRef != null) {
            for (const child of fieldRef.$children) {
                if (child.$attrs && child.$attrs['aria-label'] != null && child.$attrs['aria-label'] === 'clear icon') {
                    child.$el.setAttribute('tabIndex', '-1');
                }
            }
        }
    }

    private setCustomerData(inData?: ConfigurationValueSelect[]) {
        if (inData) {
            this.axSelectData = inData;
            if (this.axSelectDisabled === false) {
                if (inData.length === 1) {
                    this.selectValue = inData[0];
                } else {
                    this.selectValue = undefined;
                }
            }
        }
    }

    private setAxLoading() {
        this.axIsLoading = true;
    }

    private setMyMillUnits(inMachines?: ConfigurationValueSelect[]) {
        this.setMyMillLoading(false);
        if (inMachines) {
            this.mymillSelectData = inMachines;
            if (inMachines.length === 1) {
                this.selectValue = inMachines[0];
            } else {
                this.selectValue = undefined;
            }
        }
    }

    private onMyMillLoading() {
        this.setMyMillLoading(true);
    }

    private setMyMillLoading(isLoading: boolean) {
        this.mymillIsLoading = isLoading;
    }

    private getTimeString(inISO?: string, showSec: boolean = false) {
        const date = inISO != null ? new Date(inISO) : new Date();
        let timeString = showSec ? '00:00:00' : '00:00';
        if (!isNaN(date.getTime())) {
            timeString = `${date
                .getHours()
                .toString()
                .padStart(2, '0')}:${date
                .getMinutes()
                .toString()
                .padStart(2, '0')}`;
            if (showSec === true) {
                timeString += `${date
                    .getSeconds()
                    .toString()
                    .padStart(2, '0')}`;
            }
        }
        return timeString;
    }

    private getDateString(inISO: string) {
        const date = new Date(inISO);
        let dateString = '';
        if (!isNaN(date.getTime())) {
            dateString = `${this.$d(date, 'year')}-${this.$d(date, 'month')}-${this.$d(date, 'day')}`;
        }

        return dateString;
    }

    private getDateTimeString(inISO: string) {
        return `${this.getDateString(inISO)} ${this.getTimeString(inISO)}`;
    }
}
